import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  Download,
  Upload,
  Loader2,
  Database,
  Clock,
  User,
  FileArchive,
  AlertTriangle,
  CheckCircle,
  Trash2,
  History,
  Shield,
  RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BackupRestoreSettings() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  
  // State
  const [backups, setBackups] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedBackupToDelete, setSelectedBackupToDelete] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  // Check if user is OWNER
  const isOwner = user?.role === 'OWNER';
  const canBackup = user?.role === 'OWNER' || user?.role === 'ADMIN';

  // Fetch backups
  const fetchBackups = useCallback(async () => {
    if (!canBackup) return;
    
    setLoading(true);
    try {
      const response = await api.get('/admin/backups');
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Failed to fetch backups:', error);
      toast.error('Gagal memuat daftar backup');
    } finally {
      setLoading(false);
    }
  }, [api, canBackup]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    if (!canBackup) return;
    
    try {
      const response = await api.get('/admin/backup-logs?limit=20');
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  }, [api, canBackup]);

  useEffect(() => {
    fetchBackups();
    fetchLogs();
  }, [fetchBackups, fetchLogs]);

  // Create backup
  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await api.post('/admin/backup');
      toast.success(`Backup berhasil dibuat: ${response.data.filename}`);
      fetchBackups();
      fetchLogs();
    } catch (error) {
      console.error('Backup failed:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat backup');
    } finally {
      setCreating(false);
    }
  };

  // Download backup
  const handleDownload = async (backup) => {
    try {
      const response = await api.get(`/admin/backup/${backup.id}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download dimulai');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Gagal mengunduh backup');
    }
  };

  // Delete backup
  const handleDelete = async () => {
    if (!selectedBackupToDelete) return;
    
    try {
      await api.delete(`/admin/backup/${selectedBackupToDelete.id}`);
      toast.success('Backup dihapus');
      setShowDeleteDialog(false);
      setSelectedBackupToDelete(null);
      fetchBackups();
      fetchLogs();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus backup');
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        toast.error('Hanya file .zip yang diperbolehkan');
        return;
      }
      setSelectedFile(file);
      setConfirmRestore(false);
      setRestoreResult(null);
      setShowRestoreDialog(true);
    }
  };

  // Perform restore
  const handleRestore = async () => {
    if (!selectedFile || !confirmRestore) return;
    
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await api.post('/admin/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setRestoreResult(response.data);
      toast.success('Database berhasil di-restore!');
      fetchBackups();
      fetchLogs();
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error(error.response?.data?.detail || 'Gagal melakukan restore');
      setRestoreResult({ success: false, message: error.response?.data?.detail || 'Restore gagal' });
    } finally {
      setRestoring(false);
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!canBackup) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              Anda tidak memiliki akses untuk fitur backup & restore.
              <br />
              Hanya OWNER dan ADMIN yang dapat mengakses halaman ini.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Backup & Restore Database
              </CardTitle>
              <CardDescription className="mt-1">
                Kelola backup database untuk keamanan data
              </CardDescription>
            </div>
            <Button onClick={handleCreateBackup} disabled={creating}>
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Database className="h-4 w-4 mr-2" />
              )}
              Buat Backup
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Backup List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileArchive className="h-5 w-5" />
              Daftar Backup
            </CardTitle>
            <Button variant="outline" size="sm" onClick={fetchBackups} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8">
              <FileArchive className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Belum ada backup</p>
              <p className="text-sm text-muted-foreground mt-1">
                Klik "Buat Backup" untuk membuat backup pertama
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nama File</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Ukuran</TableHead>
                  <TableHead>Collections</TableHead>
                  <TableHead>Dibuat oleh</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backups.map((backup) => (
                  <TableRow key={backup.id}>
                    <TableCell className="font-mono text-sm">
                      {backup.filename}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {formatDate(backup.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>{formatSize(backup.size_bytes)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{backup.collections_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {backup.created_by}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(backup)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {isOwner && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              setSelectedBackupToDelete(backup);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Restore Section - Only for OWNER */}
      {isOwner && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Restore Database
            </CardTitle>
            <CardDescription>
              Upload file backup untuk mengembalikan database ke kondisi sebelumnya.
              <span className="text-destructive font-medium"> Perhatian: Ini akan mengganti SELURUH data!</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">Peringatan Penting:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1 text-muted-foreground">
                    <li>Restore akan <strong>menghapus semua data saat ini</strong></li>
                    <li>Data akan diganti dengan isi file backup</li>
                    <li>Sistem akan membuat safety backup sebelum restore</li>
                    <li>Operasi ini <strong>tidak dapat dibatalkan</strong></li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Input
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                className="max-w-xs"
                data-testid="restore-file-input"
              />
              <span className="text-sm text-muted-foreground">
                Format: .zip (dari backup)
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5" />
            Riwayat Aktivitas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              Belum ada aktivitas
            </p>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div className="flex items-center gap-3">
                    {log.result === 'SUCCESS' ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                    )}
                    <div>
                      <p className="text-sm font-medium">
                        {log.action === 'BACKUP' && 'Backup dibuat'}
                        {log.action === 'SAFETY_BACKUP' && 'Safety backup dibuat'}
                        {log.action === 'RESTORE' && 'Database di-restore'}
                        {log.action === 'DELETE_BACKUP' && 'Backup dihapus'}
                      </p>
                      {log.error_message && (
                        <p className="text-xs text-destructive">{log.error_message}</p>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(log.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Konfirmasi Restore Database
            </DialogTitle>
            <DialogDescription>
              {selectedFile && (
                <span className="font-mono text-sm">{selectedFile.name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {restoreResult ? (
            <div className="py-4">
              {restoreResult.success ? (
                <div className="text-center">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="font-medium text-green-600">Restore Berhasil!</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {restoreResult.restored_collections?.length || 0} collections di-restore
                  </p>
                  {restoreResult.safety_backup_id && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Safety backup: {restoreResult.safety_backup_id.slice(0, 8)}...
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
                  <p className="font-medium text-destructive">Restore Gagal</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {restoreResult.message}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                <p className="text-sm text-destructive font-medium mb-2">
                  Perhatian! Operasi ini akan:
                </p>
                <ul className="text-sm space-y-1">
                  <li>• Membuat safety backup terlebih dahulu</li>
                  <li>• Menghapus SEMUA data saat ini</li>
                  <li>• Mengganti dengan data dari backup</li>
                </ul>
              </div>
              
              <div className="flex items-start space-x-3 py-4">
                <Checkbox
                  id="confirm-restore"
                  checked={confirmRestore}
                  onCheckedChange={setConfirmRestore}
                  data-testid="confirm-restore-checkbox"
                />
                <Label
                  htmlFor="confirm-restore"
                  className="text-sm font-medium leading-tight cursor-pointer"
                >
                  Saya mengerti bahwa restore akan mengganti seluruh data dan tidak dapat dibatalkan
                </Label>
              </div>
            </>
          )}
          
          <DialogFooter>
            {restoreResult ? (
              <Button onClick={() => {
                setShowRestoreDialog(false);
                setRestoreResult(null);
                setSelectedFile(null);
                setConfirmRestore(false);
              }}>
                Tutup
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRestoreDialog(false);
                    setSelectedFile(null);
                    setConfirmRestore(false);
                  }}
                >
                  Batal
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleRestore}
                  disabled={!confirmRestore || restoring}
                  data-testid="restore-confirm-btn"
                >
                  {restoring ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Restore Database
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Backup <span className="font-mono">{selectedBackupToDelete?.filename}</span> akan dihapus permanen.
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedBackupToDelete(null)}>
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
